import React, { useState } from "react"
import { motion } from "framer-motion"
import {Search, Eye, Trash2, FileText, Edit, Download} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import VisualizarGuia from "./VisualizarGuia"

interface ListarGuiasProps {
  onEdit?: (guiaId: string) => void
}

export default function ListarGuias({ onEdit }: ListarGuiasProps) {
  const { guias, deleteGuia } = useGuias()
  const [searchTerm, setSearchTerm] = useState("")
  const [viewingGuia, setViewingGuia] = useState<string | null>(null)

  const handleDelete = async (id: string, nome: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a guia de ${nome}?`)) {
      await deleteGuia(id)
    }
  }

  const handleDownloadPDF = (id: string) => {
    setViewingGuia(id)
  }

  const filteredGuias = guias.filter(guia => 
    guia.nomeFalecido.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guia.numero.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6 print:hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800">Guias Cadastradas</h3>
          <div className="w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent w-full sm:w-80 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#004161] text-white">
                <th className="px-6 py-4 text-left text-sm font-bold border-r border-[#003450]">Número</th>
                <th className="px-6 py-4 text-left text-sm font-bold border-r border-[#003450]">Nome do Falecido</th>
                <th className="px-6 py-4 text-left text-sm font-bold border-r border-[#003450]">Data Sepultamento</th>
                <th className="px-6 py-4 text-left text-sm font-bold border-r border-[#003450]">Cemitério</th>
                <th className="px-6 py-4 text-left text-sm font-bold border-r border-[#003450]">Status</th>
                <th className="px-6 py-4 text-center text-sm font-bold">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredGuias.map((guia, index) => (
                <motion.tr
                  key={guia._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`border-b border-gray-200 hover:bg-[#004161]/5 transition-all duration-200 ${
                    index % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'
                  }`}
                >
                  <td className="px-6 py-4 text-sm text-gray-900 font-semibold border-r border-gray-200">
                    {guia.numero}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200">
                    {guia.nomeFalecido}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 border-r border-gray-200">
                    {new Date(guia.dataSepultamento).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 border-r border-gray-200">
                    {guia.cemiterio}
                  </td>
                  <td className="px-6 py-4 border-r border-gray-200">
                    <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold border border-green-200">
                      {guia.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => onEdit?.(guia._id!)}
                        className="p-2 text-[#004161] hover:bg-[#004161] hover:text-white rounded-lg transition-all duration-200 border border-[#004161]/20 hover:border-[#004161]"
                        title="Editar guia"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setViewingGuia(guia._id!)}
                        className="p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all duration-200 border border-blue-600/20 hover:border-blue-600"
                        title="Visualizar guia"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDownloadPDF(guia._id!)}
                        className="p-2 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-all duration-200 border border-green-600/20 hover:border-green-600"
                        title="Baixar PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(guia._id!, guia.nomeFalecido)}
                        className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200 border border-red-600/20 hover:border-red-600"
                        title="Excluir guia"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-3">
          {filteredGuias.map((guia, index) => (
            <motion.div
              key={guia._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="text-xs font-bold text-[#004161] mb-1">#{guia.numero}</div>
                  <div className="text-sm font-semibold text-gray-900 mb-1">{guia.nomeFalecido}</div>
                  <div className="text-xs text-gray-600">{guia.cemiterio}</div>
                </div>
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                  {guia.status}
                </span>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                {new Date(guia.dataSepultamento).toLocaleDateString("pt-BR")}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button 
                  onClick={() => onEdit?.(guia._id!)}
                  className="p-2 text-[#004161] hover:bg-[#004161] hover:text-white rounded-lg transition-all border border-[#004161]/20"
                  title="Editar"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setViewingGuia(guia._id!)}
                  className="p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all border border-blue-600/20"
                  title="Ver"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDownloadPDF(guia._id!)}
                  className="p-2 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-all border border-green-600/20"
                  title="PDF"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(guia._id!, guia.nomeFalecido)}
                  className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all border border-red-600/20"
                  title="Excluir"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredGuias.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma guia cadastrada ainda</p>
          </div>
        )}
      </motion.div>

      {/* Modal de Visualização */}
      {viewingGuia && (
        <VisualizarGuia
          guia={guias.find(g => g._id === viewingGuia)!}
          onClose={() => setViewingGuia(null)}
        />
      )}
    </div>
  )
}
